import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, User, Clock, Building2 } from "lucide-react";
import { Button } from "../ui/button";

const memberOptions = [
    {
        icon: User,
        label: "Regular Member",
        description: "City employee or community member",
        path: "/auth",
    },
    {
        icon: Clock,
        label: "Retired Professional",
        description: "Share your expertise",
        path: "/auth",
    },
    {
        icon: Building2,
        label: "Sponsor",
        description: "Support local initiatives",
        path: "/auth",
    },
];

export const CTASection = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    const navigate = useNavigate();

    return (
        <section id="get-started" className="section-padding relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-accent" />
            <div className="absolute inset-0 mesh-gradient opacity-20" />
            <motion.div
                className="absolute top-20 left-10 w-64 h-64 rounded-full bg-accent/20 blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 6, repeat: Infinity }}
            />
            <motion.div
                className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-primary-foreground/10 blur-3xl"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 8, repeat: Infinity }}
            />

            <div className="container mx-auto relative z-10" ref={ref}>
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-primary-foreground mb-4 md:mb-6">
                            Ready to Make a Difference?
                        </h2>
                        <p className="text-base sm:text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-6 md:mb-8">
                            Join thousands of civic professionals building stronger communities.
                            Choose your path and get started today.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-6 md:mb-8"
                    >
                        {memberOptions.map((option, index) => (
                            <motion.button
                                key={option.label}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                                transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                                onClick={() => navigate(option.path)}
                                className="group bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 rounded-xl md:rounded-2xl p-4 md:p-5 hover:bg-primary-foreground/20 transition-all duration-300 hover:-translate-y-1"
                            >
                                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-primary-foreground/20 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                                    <option.icon className="h-5 w-5 md:h-6 md:w-6 text-primary-foreground" />
                                </div>
                                <h3 className="font-display font-bold text-sm md:text-base text-primary-foreground mb-0.5">
                                    {option.label}
                                </h3>
                                <p className="text-primary-foreground/70 text-xs md:text-sm">
                                    {option.description}
                                </p>
                            </motion.button>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.5 }}
                    >
                        <Button
                            size="lg"
                            onClick={() => navigate("/auth")}
                            className="group bg-primary-foreground text-primary hover:bg-primary-foreground/90 h-12 md:h-14 px-6 md:px-10 text-sm md:text-base rounded-xl shadow-xl hover:shadow-2xl transition-all"
                        >
                            Get Started Now
                            <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                        <p className="text-primary-foreground/60 text-sm mt-4">
                            Free to join • No credit card required
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
